import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CreditCard, Plus, User, Mail, Calendar, Hash, Activity } from 'lucide-react';
import { payerApi } from '../lib/api/payerApi';
import { paymentMethodApi } from '../lib/api/paymentMethodApi';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { CreatePaymentMethodForm } from '../components/CreatePaymentMethodForm';
import { AdminPaymentMethodListResponse, AdminPaymentMethodSummary } from '../lib/api/types';

const PayerDetailPage: React.FC = () => {
  const { payerId } = useParams<{ payerId: string }>();
  const navigate = useNavigate();
  const [showCreatePaymentMethod, setShowCreatePaymentMethod] = useState(false);

  // Fetch payer details
  const {
    data: payerDetails,
    isLoading: isLoadingPayer,
    error: payerError,
  } = useQuery({
    queryKey: ['payer', payerId],
    queryFn: () => payerApi.getPayerDetails(payerId!),
    enabled: !!payerId,
  });

  // Fetch payment methods for the payer using admin endpoint
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useQuery({
    queryKey: ['paymentMethods', payerId],
    queryFn: () =>
      paymentMethodApi.adminListPaymentMethodsByPayerId(payerId!),
    enabled: !!payerId,
  });

  const handleBack = () => {
    navigate('/payers');
  };

  const handleCreatePaymentMethod = () => {
    setShowCreatePaymentMethod(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'default';
      case 'Inactive':
        return 'secondary';
      case 'Suspended':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getPaymentMethodTypeDisplay = (type: string) => {
    const map: Record<string, string> = {
      AMEX: 'Amex',
      DinersClub: 'Diners Club',
      CarteBancaire: 'Carte Bancaire',
      ProPayToProPay: 'ProPay to ProPay',
    };
    return map[type] ?? type;
  };

  if (isLoadingPayer) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 bg-gray-200 rounded animate-pulse" />
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="h-64 bg-gray-200 rounded animate-pulse" />
            <div className="h-64 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (payerError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">
            Failed to load payer details. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (!payerDetails) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-blue-800">Payer not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Payers
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{payerDetails.Name}</h1>
              <p className="text-muted-foreground">Payer Details</p>
            </div>
          </div>
          <Badge variant={getStatusBadgeVariant(payerDetails.Status)}>
            {payerDetails.Status}
          </Badge>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Payer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Payer Information
              </CardTitle>
              <CardDescription>
                Basic information about this payer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <div className="flex items-center gap-3">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Account ID</p>
                    <p className="text-sm text-muted-foreground">{payerDetails.PayerAccountId}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Name</p>
                    <p className="text-sm text-muted-foreground">{payerDetails.Name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{payerDetails.EmailAddress}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">External ID</p>
                    <p className="text-sm text-muted-foreground">{payerDetails.ExternalId1}</p>
                  </div>
                </div>

                {payerDetails.ExternalId2 && (
                  <div className="flex items-center gap-3">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">External ID 2</p>
                      <p className="text-sm text-muted-foreground">{payerDetails.ExternalId2}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Created</p>
                    <p className="text-sm text-muted-foreground">{formatDate(payerDetails.CreatedDate)}</p>
                  </div>
                </div>

                {payerDetails.LastModifiedDate && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Last Modified</p>
                      <p className="text-sm text-muted-foreground">{formatDate(payerDetails.LastModifiedDate)}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <Badge variant={getStatusBadgeVariant(payerDetails.Status)}>
                      {payerDetails.Status}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Payment Methods
                  </CardTitle>
                  <CardDescription>
                    Manage payment methods for this payer
                  </CardDescription>
                </div>
                <Button onClick={handleCreatePaymentMethod} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPaymentMethods ? (
                <div className="space-y-3">
                  <div className="h-16 bg-gray-200 rounded animate-pulse" />
                  <div className="h-16 bg-gray-200 rounded animate-pulse" />
                  <div className="h-16 bg-gray-200 rounded animate-pulse" />
                </div>
              ) : paymentMethodsError ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800">
                    Failed to load payment methods. This feature requires partner authentication.
                  </p>
                </div>
              ) : paymentMethods?.PaymentMethods?.length ? (
                <div className="space-y-3">
                  {paymentMethods.PaymentMethods.map((method: AdminPaymentMethodSummary) => (
                    <div
                      key={method.PaymentMethodId}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/payment-methods/${method.PaymentMethodId}`)}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{method.AccountName}</p>
                          <p className="text-xs text-muted-foreground">
                            {getPaymentMethodTypeDisplay(method.PaymentMethodType)} •
                            Priority: {method.Priority} •
                            Status: {method.Status}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Created: {formatDate(method.CreatedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {method.Protected && (
                          <Badge variant="secondary" className="text-xs">
                            Protected
                          </Badge>
                        )}
                        <Button variant="outline" size="sm" onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/payment-methods/${method.PaymentMethodId}`);
                        }}>
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground mb-4">
                    No payment methods found for this payer.
                  </p>
                  <Button onClick={handleCreatePaymentMethod} variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Payment Method
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Method Creation Modal */}
        <Dialog open={showCreatePaymentMethod} onOpenChange={setShowCreatePaymentMethod}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Payment Method</DialogTitle>
              <DialogDescription>
                Create a new payment method for {payerDetails?.Name}
              </DialogDescription>
            </DialogHeader>
            <CreatePaymentMethodForm
              payerId={payerId!}
              onSuccess={() => {
                setShowCreatePaymentMethod(false);
                refetchPaymentMethods();
              }}
              onCancel={() => setShowCreatePaymentMethod(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PayerDetailPage;
